import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Plus, Pencil, Trash2, Eye, EyeOff, Check, X } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY_FORM = {
  title: "", slug: "", meta_description: "", cover_image_url: "", content: "", published: true
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

export default function AdminBlog() {
  const { user, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | "new" | post.slug
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const isAdmin = user?.is_admin;

  const loadPosts = async () => {
    try {
      const r = await api.get("/blog/posts?drafts=true");
      setPosts(r.data || []);
    } catch { toast.error("Could not load posts"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) loadPosts(); }, [isAdmin]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-400" /></div>;
  if (!user || !isAdmin) return <Navigate to="/" replace />;

  const openNew = () => { setForm(EMPTY_FORM); setEditing("new"); };
  const openEdit = (post) => {
    setForm({ title: post.title, slug: post.slug, meta_description: post.meta_description,
               cover_image_url: post.cover_image_url || "", content: post.content, published: post.published });
    setEditing(post.slug);
  };
  const cancelEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const autoSlug = (title) => title.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");

  const handleSave = async () => {
    if (!form.title.trim() || !form.meta_description.trim() || !form.content.trim()) {
      toast.error("Title, meta description, and content are required"); return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/blog/posts", { ...form, slug: form.slug || autoSlug(form.title) });
        toast.success("Post created");
      } else {
        await api.put(`/blog/posts/${editing}`, form);
        toast.success("Post updated");
      }
      cancelEdit();
      loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm("Delete this post permanently?")) return;
    setDeleting(slug);
    try {
      await api.delete(`/blog/posts/${slug}`);
      toast.success("Post deleted");
      setPosts(prev => prev.filter(p => p.slug !== slug));
    } catch { toast.error("Delete failed"); }
    finally { setDeleting(null); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="admin-blog-page">
      <Nav />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-20">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl text-stone-900">Blog Admin</h1>
            <p className="text-stone-400 text-sm mt-1">{posts.length} post{posts.length !== 1 ? "s" : ""} total</p>
          </div>
          {!editing && (
            <button onClick={openNew} className="btn-primary inline-flex items-center gap-2" data-testid="new-post-btn">
              <Plus className="w-4 h-4" /> New Post
            </button>
          )}
        </div>

        {/* ── Editor form ── */}
        {editing && (
          <div className="rounded-2xl border border-stone-200 bg-white p-6 mb-8 shadow-sm" data-testid="post-form">
            <h2 className="font-display text-xl mb-5">{editing === "new" ? "New Post" : "Edit Post"}</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Title *</label>
                  <input
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value, slug: f.slug || autoSlug(e.target.value) }))}
                    placeholder="Post title"
                    data-testid="form-title"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-500 mb-1">Slug</label>
                  <input
                    className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    value={form.slug}
                    onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                    placeholder="auto-generated-from-title"
                    data-testid="form-slug"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Meta description *</label>
                <input
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  value={form.meta_description}
                  onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))}
                  placeholder="SEO description (150–160 characters)"
                  maxLength={160}
                  data-testid="form-meta"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Cover image URL</label>
                <input
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  value={form.cover_image_url}
                  onChange={e => setForm(f => ({ ...f, cover_image_url: e.target.value }))}
                  placeholder="https://images.unsplash.com/..."
                  data-testid="form-cover"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Content (HTML) *</label>
                <textarea
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-200 min-h-[320px] resize-y"
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="<p>Your article content in HTML...</p>"
                  data-testid="form-content"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="published-check"
                  checked={form.published}
                  onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600"
                  data-testid="form-published"
                />
                <label htmlFor="published-check" className="text-sm text-stone-600">Published (visible to public)</label>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={handleSave} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-post-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {saving ? "Saving…" : "Save post"}
              </button>
              <button onClick={cancelEdit} className="btn-ghost inline-flex items-center gap-2 border border-stone-200" data-testid="cancel-post-btn">
                <X className="w-4 h-4" /> Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Posts table ── */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
        ) : posts.length === 0 ? (
          <p className="text-center text-stone-400 py-16">No posts yet. Create your first one!</p>
        ) : (
          <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="posts-table">
            {posts.map((post, i) => (
              <div
                key={post.slug}
                className={`flex items-center gap-4 px-5 py-4 ${i < posts.length - 1 ? "border-b border-stone-100" : ""}`}
                data-testid={`post-row-${post.slug}`}
              >
                {post.cover_image_url && (
                  <img src={post.cover_image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0 hidden sm:block" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-800 truncate">{post.title}</p>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-stone-400">
                    <span className="font-mono text-stone-300">{post.slug}</span>
                    <span>{fmt(post.published_at)}</span>
                    <span className="flex items-center gap-1">
                      {post.published
                        ? <><Eye className="w-3 h-3 text-green-500" /><span className="text-green-600">Published</span></>
                        : <><EyeOff className="w-3 h-3 text-stone-300" /><span>Draft</span></>}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={`/blog/${post.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg text-stone-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                    title="View post"
                    data-testid={`view-post-${post.slug}`}
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => openEdit(post)}
                    className="p-2 rounded-lg text-stone-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                    data-testid={`edit-post-${post.slug}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(post.slug)}
                    disabled={deleting === post.slug}
                    className="p-2 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    data-testid={`delete-post-${post.slug}`}
                  >
                    {deleting === post.slug
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
